<script setup lang="ts">
import { inject, computed, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useOpsStore } from '@/stores/ops'
import { useAssignmentsStore } from '@/stores/assignments'
import { useTeamsStore } from '@/stores/teams'
import { usePersonnelStore } from '@/stores/personnel'
import { useFormsStore } from '@/stores/forms'
import { useSyncStore } from '@/stores/sync'
import FormShell from '@/components/forms/FormShell.vue'
import FosInput from '@/components/ui/FosInput.vue'
import FosTextarea from '@/components/ui/FosTextarea.vue'

const route = useRoute()
const opsStore = useOpsStore()
const assignmentsStore = useAssignmentsStore()
const teamsStore = useTeamsStore()
const personnelStore = usePersonnelStore()
const formsStore = useFormsStore()
const syncStore = useSyncStore()
const incidentId = inject<string>('incidentId', '')
const taId = route.params.taId as string

const formKey = computed(() => `SAR111_${taId}`)
const formData = computed(() => formsStore.getFormData(formKey.value))

const op = computed(() => opsStore.selectedOp)

// Live derived values from stores
const teamAssignment = computed(() =>
  assignmentsStore.teamAssignments.find((ta) => ta.ta_id === taId),
)
const team = computed(() => {
  const teamId = teamAssignment.value?.team_id
  return teamId ? teamsStore.teams.find((t) => t.team_id === teamId) : undefined
})
const assignment = computed(() => {
  const assignmentId = teamAssignment.value?.assignment_id
  return assignmentId ? assignmentsStore.assignments.find((a) => a.assignment_id === assignmentId) : undefined
})
const teamMembers = computed(() => {
  const teamId = teamAssignment.value?.team_id
  if (!teamId) return []
  return personnelStore.roster.filter((p) => p.team_id === teamId && !p.time_out)
})
const handlerName = computed(() => {
  const handler = teamMembers.value.find((p) => {
    const role = (p as unknown as Record<string, string>).role
    return typeof role === 'string' && role.toLowerCase().includes('handler')
  })
  if (handler) return `${handler.first} ${handler.last}`
  const first = teamMembers.value[0]
  return first ? `${first.first} ${first.last}` : ''
})
const teamMembersText = computed(() =>
  teamMembers.value.map((p) => `${p.first} ${p.last}`).join(', '),
)

// Pre-populate from team_assignment timestamps on first open
watch(
  teamAssignment,
  async (ta) => {
    if (!ta || !incidentId) return
    const existing = formsStore.getFormData(formKey.value)
    if (existing['_initialized']) return
    const writes: Promise<void>[] = [
      formsStore.saveFormField(incidentId, formKey.value, '_initialized', true),
    ]
    if (ta.started_at && !existing['alert_time']) {
      const d = (ta.started_at as { toDate?: () => Date }).toDate?.()
      if (d) writes.push(formsStore.saveFormField(incidentId, formKey.value, 'alert_time', d.toTimeString().slice(0, 5)))
    }
    await Promise.all(writes)
  },
  { immediate: true },
)

function field(key: string): string {
  const v = formData.value[key]
  return v != null ? String(v) : ''
}

async function save(fieldPath: string, event: Event) {
  if (!incidentId) return
  const value = (event.target as HTMLInputElement | HTMLTextAreaElement).value
  syncStore.pendingWrites++
  try {
    await formsStore.saveFormField(incidentId, formKey.value, fieldPath, value)
  } finally {
    syncStore.pendingWrites--
  }
}
</script>

<template>
  <FormShell
    :form-type="formKey"
    title="SAR 111 · K9 Team Debrief"
    back-route="incident-form-sar111-list"
    back-label="K9 Debriefs"
    :back-params="{ id: incidentId }"
  >
    <div class="max-w-[900px] mx-auto px-7 py-[22px] pb-16 flex flex-col gap-[18px]">
      <!-- Section head -->
      <div class="flex items-end gap-3.5 flex-wrap">
        <div class="flex flex-col gap-0.5">
          <span class="font-mono text-[11px] tracking-[0.12em] uppercase text-primary">SAR 111 · K9 Team Debrief</span>
          <h2 class="font-headline font-black text-[26px] uppercase tracking-tight text-on-surface m-0">
            {{ team?.team_identifier ?? 'K9 Team Debrief' }}
          </h2>
        </div>
        <p class="flex-1 min-w-[200px] text-[13px] text-outline m-0">K9 team post-assignment debrief for {{ assignment?.assignment_name ?? 'this assignment' }}.</p>
      </div>

      <!-- Team / Assignment (read-only, from stores) -->
      <div class="bg-surface border border-border rounded flex flex-col">
        <div class="flex items-center gap-2 px-3.5 py-2.5 border-b border-border/60">
          <h3 class="font-mono font-semibold text-[11px] tracking-[0.12em] uppercase text-on-surface m-0">Team / Assignment</h3>
          <span class="font-mono text-[9px] tracking-[0.10em] uppercase text-outline px-1.5 py-0.5 border border-border rounded bg-surface-container-low ml-auto">Read-only</span>
        </div>
        <div class="p-3.5 grid grid-cols-2 gap-3">
          <div>
            <label class="font-mono text-[10px] tracking-[0.10em] uppercase text-outline block mb-1">Team Identifier</label>
            <p class="font-mono text-[13px] text-on-surface px-2.5 py-2 bg-surface-container-low border border-border rounded">{{ team?.team_identifier ?? '—' }}</p>
          </div>
          <div>
            <label class="font-mono text-[10px] tracking-[0.10em] uppercase text-outline block mb-1">Operational Period</label>
            <p class="font-mono text-[13px] text-on-surface px-2.5 py-2 bg-surface-container-low border border-border rounded">{{ op ? `OP ${op.op_number}` : '—' }}</p>
          </div>
          <div class="col-span-2">
            <label class="font-mono text-[10px] tracking-[0.10em] uppercase text-outline block mb-1">Assignment</label>
            <p class="font-body text-[13px] text-on-surface px-2.5 py-2 bg-surface-container-low border border-border rounded">{{ assignment?.assignment_name ?? '—' }}</p>
          </div>
          <div>
            <label class="font-mono text-[10px] tracking-[0.10em] uppercase text-outline block mb-1">Handler</label>
            <p class="font-body text-[13px] text-on-surface px-2.5 py-2 bg-surface-container-low border border-border rounded">{{ handlerName || '—' }}</p>
          </div>
          <div>
            <label class="font-mono text-[10px] tracking-[0.10em] uppercase text-outline block mb-1">Team Members</label>
            <p class="font-body text-[13px] text-on-surface px-2.5 py-2 bg-surface-container-low border border-border rounded">{{ teamMembersText || '—' }}</p>
          </div>
        </div>
      </div>

      <!-- K9 Details -->
      <div class="bg-surface border border-border rounded flex flex-col">
        <div class="flex items-center gap-2 px-3.5 py-2.5 border-b border-border/60">
          <h3 class="font-mono font-semibold text-[11px] tracking-[0.12em] uppercase text-on-surface m-0">K9 Details</h3>
        </div>
        <div class="p-3.5 grid grid-cols-3 gap-3">
          <div>
            <label class="font-mono text-[10px] tracking-[0.10em] uppercase text-outline block mb-1">Dog Name</label>
            <FosInput
              :model-value="field('dog_name')"
              placeholder="Dog's name…"
              @blur="save('dog_name', $event)"
            />
          </div>
          <div>
            <label class="font-mono text-[10px] tracking-[0.10em] uppercase text-outline block mb-1">Breed</label>
            <FosInput
              :model-value="field('breed')"
              placeholder="Breed…"
              @blur="save('breed', $event)"
            />
          </div>
          <div>
            <label class="font-mono text-[10px] tracking-[0.10em] uppercase text-outline block mb-1">Certification Level</label>
            <FosInput
              :model-value="field('certification')"
              placeholder="Certification…"
              @blur="save('certification', $event)"
            />
          </div>
        </div>
      </div>

      <!-- Search Area -->
      <div class="bg-surface border border-border rounded flex flex-col">
        <div class="flex items-center gap-2 px-3.5 py-2.5 border-b border-border/60">
          <h3 class="font-mono font-semibold text-[11px] tracking-[0.12em] uppercase text-on-surface m-0">Search Area</h3>
        </div>
        <div class="p-3.5 grid grid-cols-2 gap-3">
          <div class="col-span-2">
            <label class="font-mono text-[10px] tracking-[0.10em] uppercase text-outline block mb-1">Area Description</label>
            <FosTextarea
              :model-value="field('area_description')"
              placeholder="Describe the search area…"
              :rows="2"
              @blur="save('area_description', $event)"
            />
          </div>
          <div>
            <label class="font-mono text-[10px] tracking-[0.10em] uppercase text-outline block mb-1">Terrain</label>
            <FosInput
              :model-value="field('terrain')"
              placeholder="e.g. Dense forest, hillside…"
              @blur="save('terrain', $event)"
            />
          </div>
          <div>
            <label class="font-mono text-[10px] tracking-[0.10em] uppercase text-outline block mb-1">Alert Time</label>
            <FosInput
              type="time"
              :model-value="field('alert_time')"
              @blur="save('alert_time', $event)"
            />
          </div>
        </div>
      </div>

      <!-- Search Efforts -->
      <div class="bg-surface border border-border rounded flex flex-col">
        <div class="flex items-center gap-2 px-3.5 py-2.5 border-b border-border/60">
          <h3 class="font-mono font-semibold text-[11px] tracking-[0.12em] uppercase text-on-surface m-0">Search Efforts & Findings</h3>
        </div>
        <div class="p-3.5">
          <FosTextarea
            :model-value="field('search_efforts')"
            placeholder="Describe K9 search efforts, any alerts, indications, or negative findings…"
            :rows="4"
            @blur="save('search_efforts', $event)"
          />
        </div>
      </div>

      <!-- Hazards -->
      <div class="bg-surface border border-border rounded flex flex-col">
        <div class="flex items-center gap-2 px-3.5 py-2.5 border-b border-border/60">
          <h3 class="font-mono font-semibold text-[11px] tracking-[0.12em] uppercase text-on-surface m-0">Hazards & Problems</h3>
        </div>
        <div class="p-3.5">
          <FosTextarea
            :model-value="field('hazards')"
            placeholder="Hazards encountered or operational issues…"
            :rows="3"
            @blur="save('hazards', $event)"
          />
        </div>
      </div>

      <!-- Suggestions -->
      <div class="bg-surface border border-border rounded flex flex-col">
        <div class="flex items-center gap-2 px-3.5 py-2.5 border-b border-border/60">
          <h3 class="font-mono font-semibold text-[11px] tracking-[0.12em] uppercase text-on-surface m-0">Suggestions</h3>
        </div>
        <div class="p-3.5">
          <FosTextarea
            :model-value="field('suggestions')"
            placeholder="Recommendations for continued operations…"
            :rows="3"
            @blur="save('suggestions', $event)"
          />
        </div>
      </div>
    </div>
  </FormShell>
</template>
